import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Steps.css';
import './StepWelcome.css';

/* ═══════════════════════════════════════════════════════════════
   KURZGESAGT RIDER — full human on scooter
   ═══════════════════════════════════════════════════════════════ */

/* ─── Color palette (same as JamaStoryOutline) ─── */
const TEAL = '#1BC7B1';
const ORANGE = '#FF6A00';
const BLUE = '#6C7AE0';
const PINK = '#E85D75';
const EYE = '#1A1A2E';
const W = '#FFFFFF';
const DK = '#2D2D3A';
const SK1 = '#F4A4B0';
const SK2 = '#C8896A';

function WelcomeRider() {
  return (
    <svg viewBox="0 0 320 270" className="wr-svg">
      <motion.g
        animate={{ x: 0, opacity: 1 }}
        initial={{ x: -60, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 150, damping: 20 }}
      >
      <motion.g
        animate={{ y: [-1.5, 1.5, -1.5] }}
        transition={{ duration: 0.4, repeat: Infinity }}
      >
        {/* Ground shadow */}
        <ellipse cx="160" cy="250" rx="95" ry="6" fill="rgba(0,0,0,0.08)" />

        {/* ═══ SCOOTER ══════════════════════════════════════════ */}

        {/* ── Wheels ── */}
        <circle cx="78" cy="224" r="24" fill="#3D3D4D" />
        <circle cx="78" cy="224" r="21" fill="#4D4D5C" />
        <circle cx="78" cy="224" r="14" fill="#5E5E6E" />
        <circle cx="78" cy="224" r="6" fill="#888" />
        <circle cx="78" cy="224" r="2.5" fill="#AAA" />
        <path d="M58 218 Q78 210 98 218" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />

        <circle cx="246" cy="224" r="24" fill="#3D3D4D" />
        <circle cx="246" cy="224" r="21" fill="#4D4D5C" />
        <circle cx="246" cy="224" r="14" fill="#5E5E6E" />
        <circle cx="246" cy="224" r="6" fill="#888" />
        <circle cx="246" cy="224" r="2.5" fill="#AAA" />
        <path d="M226 218 Q246 210 266 218" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />

        {/* ── Fenders ── */}
        <path d="M54 216 Q54 198 78 198 Q102 198 102 216" fill={TEAL} />
        <path d="M224 216 Q224 198 246 198 Q268 198 268 216" fill={TEAL} />

        {/* ── Scooter body ── */}
        <rect x="96" y="208" width="118" height="10" rx="5" fill={TEAL} />
        <rect x="156" y="208" width="58" height="10" rx="5" fill="rgba(0,0,0,0.05)" />

        <path d="M90 212 Q90 174 112 162 L176 162 Q186 162 186 174 L186 212 Z" fill={TEAL} />
        <path d="M146 212 Q146 174 164 162 L176 162 Q186 162 186 174 L186 212 Z" fill="rgba(0,0,0,0.05)" />

        <path d="M210 212 L210 172 Q210 150 232 148 L244 148 Q256 148 256 170 L256 212 Z" fill={TEAL} />
        <path d="M238 212 L238 172 Q238 152 246 148 L244 148 Q256 148 256 170 L256 212 Z" fill="rgba(0,0,0,0.06)" />

        {/* Seat */}
        <path d="M120 160 Q120 148 136 148 L182 148 Q192 148 190 160 Z" fill="#3A3A4A" />
        <path d="M124 154 Q124 146 138 146 L180 146 Q188 146 186 154 Z" fill="#4D4D5C" />
        <path d="M136 148 Q156 144 178 148" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />

        {/* Handlebar */}
        <rect x="238" y="130" width="5" height="34" rx="2.5" fill="#777" />
        <rect x="222" y="124" width="34" height="7" rx="3.5" fill="#5E5E6E" />
        <rect x="218" y="122" width="9" height="11" rx="4.5" fill="#3A3A48" />
        <rect x="251" y="122" width="9" height="11" rx="4.5" fill="#3A3A48" />

        {/* Mirror */}
        <rect x="254" y="113" width="2" height="10" rx="1" fill="#888" />
        <ellipse cx="255.5" cy="111" rx="4.5" ry="3" fill="#A8B8CC" />
        <ellipse cx="256" cy="110" rx="2.5" ry="1.5" fill="rgba(255,255,255,0.2)" />

        {/* Headlight */}
        <ellipse cx="253" cy="174" rx="6" ry="8" fill="#FFE882" />
        <ellipse cx="253" cy="174" rx="3.5" ry="5" fill="#FFFDE0" />

        {/* Tail light */}
        <rect x="86" y="198" width="9" height="5" rx="2.5" fill="#FF6B6B" />

        {/* Exhaust */}
        <path d="M74 210 Q74 206 78 206 L92 206 Q96 206 96 210 L96 214 Q96 218 92 218 L78 218 Q74 218 74 214 Z" fill="#777" />

        {/* ── Delivery box ── */}
        <rect x="88" y="148" width="36" height="3" rx="1.5" fill="#777" />
        <rect x="74" y="96" width="54" height="54" rx="8" fill={ORANGE} />
        <rect x="74" y="96" width="54" height="12" rx="6" fill="#D85300" />
        <rect x="74" y="108" width="27" height="42" rx="4" fill="rgba(255,255,255,0.06)" />
        <rect x="101" y="96" width="27" height="54" rx="4" fill="rgba(0,0,0,0.06)" />
        <image href="/logo_jama_sinfondo.png" x="82" y="114" width="36" height="22" />

        {/* ═══ RIDER (profile, facing right) ═══════════════════ */}

        {/* ── Back leg (behind, darker for depth) ── */}
        <line x1="156" y1="154" x2="192" y2="182" stroke="#4A5A88" strokeWidth="15" strokeLinecap="round" />
        <line x1="192" y1="182" x2="194" y2="204" stroke="#4A5A88" strokeWidth="13" strokeLinecap="round" />
        <rect x="186" y="202" width="20" height="9" rx="4.5" fill="#2D2D3A" opacity="0.8" />

        {/* ── Front leg ── */}
        <line x1="162" y1="154" x2="200" y2="182" stroke={BLUE} strokeWidth="17" strokeLinecap="round" />
        <line x1="200" y1="182" x2="202" y2="204" stroke={BLUE} strokeWidth="15" strokeLinecap="round" />
        <rect x="192" y="202" width="24" height="10" rx="5" fill="#2D2D3A" />
        <rect x="192" y="210" width="24" height="3" rx="1.5" fill="#222230" />

        {/* ── Torso (teal delivery jacket) ── */}
        <path d="M150,154 L154,98 Q158,86 170,84 L180,84 Q192,86 190,98 L196,154 Z" fill={TEAL} />
        <path d="M180,84 Q192,86 190,98 L196,154 L180,154 Z" fill="rgba(0,0,0,0.08)" />
        <path d="M160,88 Q168,82 176,82 Q184,82 186,88" fill="#15A89D" stroke="#15A89D" strokeWidth="1" />
        <line x1="174" y1="90" x2="174" y2="142" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

        {/* ── Back arm (behind body, darker) ── */}
        <line x1="172" y1="96" x2="200" y2="114" stroke="#13A093" strokeWidth="12" strokeLinecap="round" />
        <line x1="200" y1="114" x2="220" y2="126" stroke={SK2} strokeWidth="10" strokeLinecap="round" />
        <circle cx="220" cy="126" r="5" fill={SK2} />

        {/* ── Front arm ── */}
        <line x1="178" y1="96" x2="206" y2="116" stroke={TEAL} strokeWidth="13" strokeLinecap="round" />
        <line x1="206" y1="116" x2="224" y2="128" stroke={SK1} strokeWidth="11" strokeLinecap="round" />
        <circle cx="224" cy="128" r="6" fill={SK1} />
        <path d="M220,126 Q222,122 225,125" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="0.8" />

        {/* ── Neck ── */}
        <line x1="172" y1="84" x2="176" y2="74" stroke={SK1} strokeWidth="10" strokeLinecap="round" />

        {/* ── Head (profile facing right) ── */}
        <ellipse cx="178" cy="56" rx="16" ry="20" fill={SK1} />
        <path d="M182,48 Q198,50 198,60 Q198,72 186,74 Q178,74 178,68 Z" fill={SK1} />
        <ellipse cx="162" cy="58" rx="4" ry="5.5" fill={SK1} />
        <ellipse cx="162" cy="58" rx="2.5" ry="3.5" fill="rgba(0,0,0,0.04)" />

        {/* ── Helmet (modern, aerodynamic) ── */}
        {/* Main shell — smooth, aerodynamic profile */}
        <path d="M156,50 Q154,22 174,16 Q196,14 206,28 Q212,38 208,52 Q206,56 198,58 Q180,62 162,58 Q156,56 156,50 Z" fill={ORANGE} />
        {/* Right side depth shadow */}
        <path d="M196,16 Q206,22 210,36 Q212,46 208,52 Q206,56 198,58 L196,58 Q202,56 204,50 Q208,40 204,30 Q200,20 196,16 Z" fill="#D85300" />
        {/* Top highlight — semi-glossy finish */}
        <path d="M164,28 Q174,18 190,18 Q198,20 202,28" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" strokeLinecap="round" />
        {/* Ventilation slits — front */}
        <line x1="178" y1="22" x2="182" y2="24" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="184" y1="21" x2="188" y2="23" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="190" y1="22" x2="194" y2="24" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" strokeLinecap="round" />
        {/* Aerodynamic side line / design cut */}
        <path d="M160,46 Q172,42 188,42 Q200,42 208,46" fill="none" stroke="#D85300" strokeWidth="1.8" strokeLinecap="round" />
        {/* Bottom edge trim — dark rubber */}
        <path d="M158,54 Q168,60 180,60 Q192,60 204,54" fill="none" stroke={DK} strokeWidth="2.5" strokeLinecap="round" />
        {/* Visor — tinted, slightly transparent */}
        <path d="M194,48 Q204,44 210,48 L210,56 Q204,62 194,58 Z" fill="rgba(74,144,217,0.35)" />
        <path d="M196,48 Q202,46 206,48 L206,52 Q202,56 196,54 Z" fill="rgba(255,255,255,0.15)" />
        {/* Jama logo — small "J" on side */}
        <text x="172" y="42" fill="rgba(255,255,255,0.7)" fontSize="8" fontWeight="800" fontFamily="Nunito, sans-serif">J</text>
        {/* Subtle glow stripe accent */}
        <path d="M162,50 Q172,46 186,46 Q196,46 204,50" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

        {/* ── Face (profile) ── */}
        <ellipse cx="190" cy="52" rx="3" ry="3.5" fill={EYE} />
        <circle cx="191" cy="51" r="1" fill={W} />
        <path d="M186,48 Q190,46 194,48" fill="none" stroke="#5A4A3A" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M196,56 Q199,58 198,62" fill="none" stroke={SK2} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M192,66 Q195,68 197,66" fill="none" stroke={PINK} strokeWidth="1.8" strokeLinecap="round" />
        <ellipse cx="194" cy="62" rx="4" ry="2.5" fill="rgba(232,93,117,0.12)" />

        {/* ── Speech bubble ── */}
        <g>
          <path d="M212,10 Q212,2 222,2 L280,2 Q290,2 290,10 L290,28 Q290,36 280,36 L240,36 L234,44 L236,36 L222,36 Q212,36 212,28 Z" fill={W} />
          <path d="M212,10 Q212,2 222,2 L280,2 Q290,2 290,10 L290,28 Q290,36 280,36 L240,36 L234,44 L236,36 L222,36 Q212,36 212,28 Z" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="1" />
          <text x="251" y="23" textAnchor="middle" fill="#1A1A2E" fontSize="10" fontWeight="700" fontFamily="Nunito, sans-serif">{'¡En camino!'}</text>
        </g>
      </motion.g>

      {/* Speed lines */}
      {[0, 1, 2, 3].map(i => (
        <motion.rect key={i}
          x={6 + i * 3} y={142 + i * 22}
          width={20 + i * 4} height={3} rx={1.5}
          fill={W}
          animate={{ x: [-24, 0, 24], opacity: [0, 0.45, 0] }}
          transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.12 }}
        />
      ))}
      </motion.g>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PROCESS STEPS DATA
   ═══════════════════════════════════════════════════════════════ */
const processSteps = [
  {
    num: 1,
    icon: '\u270D\uFE0F',
    title: 'Reg\u00EDstrate',
    desc: 'Crea tu cuenta para comenzar el proceso de inscripci\u00F3n.',
  },
  {
    num: 2,
    icon: '\uD83D\uDCCB',
    title: 'Llena el formulario',
    desc: 'Completa tus datos personales y la informaci\u00F3n de tu veh\u00EDculo.',
  },
  {
    num: 3,
    icon: '\uD83D\uDD0D',
    title: 'Revisamos tu documentaci\u00F3n',
    desc: 'Nuestro equipo verificar\u00E1 tus documentos para validar tu registro.',
  },
  {
    num: 4,
    icon: '\uD83D\uDCDE',
    title: 'Capacitaci\u00F3n',
    desc: 'Te explicaremos c\u00F3mo usar la app y c\u00F3mo recibir pedidos.',
  },
  {
    num: 5,
    icon: '\uD83D\uDE80',
    title: '\u00A1Comienza a repartir!',
    desc: 'Inicia sesi\u00F3n en la app y empieza a generar ingresos.',
  },
];

/* ═══════════════════════════════════════════════════════════════
   BENEFITS DATA
   ═══════════════════════════════════════════════════════════════ */
const benefits = [
  { icon: '\uD83D\uDCB0', title: 'Ganancias atractivas', desc: 'Pagos semanales a tu cuenta', color: 'orange' },
  { icon: '\uD83D\uDD50', title: 'Horarios flexibles', desc: 'Trabaja cuando quieras', color: 'teal' },
  { icon: '\uD83D\uDCF1', title: 'App intuitiva', desc: 'Gestiona todo desde el celular', color: 'blue' },
  { icon: '\uD83C\uDF81', title: 'Bonos exclusivos', desc: 'Incentivos por cada entrega', color: 'pink' },
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const StepWelcome = ({ onNext }) => {
  const [activeProcess, setActiveProcess] = useState(0);
  const [costsAcknowledged, setCostsAcknowledged] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveProcess(prev => (prev + 1) % processSteps.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const currentStep = processSteps[activeProcess];

  return (
    <div className="wr">
      {/* ── LEFT COLUMN ── */}
      <div className="wr__left">
        {/* Header */}
        <motion.div
          className="wr__header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="wr__badge">
            <motion.span
              className="wr__badge-dot"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="wr__badge-text">Registro abierto</span>
          </div>

          <h1 className="wr__title">
            <span className="wr__title-line">Convi&eacute;rtete en</span>
            <span className="wr__title-line">repartidor <span className="wr__brand">JAMA</span></span>
          </h1>

          <p className="wr__subtitle">
            Genera ingresos con total flexibilidad. Sin horarios fijos,
            sin jefes &mdash; t&uacute; tienes el control.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="wr__stats"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <div className="wr__stat">
            <span className="wr__stat-number wr__stat-number--orange">$150+</span>
            <span className="wr__stat-label">Promedio semanal</span>
          </div>
          <div className="wr__stat">
            <span className="wr__stat-number wr__stat-number--teal">24/7</span>
            <span className="wr__stat-label">Disponibilidad</span>
          </div>
          <div className="wr__stat">
            <span className="wr__stat-number">5 min</span>
            <span className="wr__stat-label">Tiempo de registro</span>
          </div>
        </motion.div>

        {/* Benefits 2x2 */}
        <div className="wr__benefits">
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              className="wr__benefit"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.08, type: 'spring', stiffness: 200 }}
            >
              <span className={`wr__benefit-icon wr__benefit-icon--${b.color}`}>{b.icon}</span>
              <div className="wr__benefit-text">
                <strong>{b.title}</strong>
                <span>{b.desc}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Docs */}
        <motion.div
          className="wr__docs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <span className="wr__docs-icon">{'\uD83D\uDCC4'}</span>
          <div className="wr__docs-content">
            <p className="wr__docs-title">Ten a mano tus documentos</p>
            <p className="wr__docs-text">C&eacute;dula, licencia de conducir y matr&iacute;cula del veh&iacute;culo vigentes. La matr&iacute;cula debe estar <strong>a tu nombre</strong>.</p>
          </div>
        </motion.div>

        {/* Costs warning — MANDATORY */}
        <motion.div
          className="wr__costs"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className="wr__costs-header">
            <span className="wr__costs-icon">{'\uD83D\uDCB0'}</span>
            <div className="wr__costs-title-wrap">
              <p className="wr__costs-title">Costos para activar tu cuenta</p>
              <p className="wr__costs-sub">Estos pagos son requisito para recibir pedidos y comenzar a trabajar</p>
            </div>
          </div>

          <div className="wr__costs-list">
            <div className="wr__costs-item">
              <span className="wr__costs-amount">$40</span>
              <div className="wr__costs-detail">
                <strong>Inscripción</strong>
                <span>Pago único al inicio del proceso</span>
              </div>
            </div>
            <div className="wr__costs-item">
              <span className="wr__costs-amount">{'\uD83C\uDF92'}</span>
              <div className="wr__costs-detail">
                <strong>Mochila JAMA</strong>
                <span>Se paga después del periodo de prueba</span>
              </div>
            </div>
          </div>

          <label className="wr__costs-check">
            <input
              type="checkbox"
              checked={costsAcknowledged}
              onChange={(e) => setCostsAcknowledged(e.target.checked)}
            />
            <span>
              Entiendo que debo pagar los <strong>$40 de inscripción</strong> para
              activar mi cuenta y que sin este pago <strong>no puedo continuar</strong>.
            </span>
          </label>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="wr__cta"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, type: 'spring' }}
        >
          <motion.button
            className="wr__btn"
            onClick={onNext}
            disabled={!costsAcknowledged}
            whileHover={costsAcknowledged ? { scale: 1.02 } : {}}
            whileTap={costsAcknowledged ? { scale: 0.97 } : {}}
          >
            Comenzar registro
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >{'\u2192'}</motion.span>
          </motion.button>
          <p className="wr__time">
            <span className="wr__time-icon">{'\u26A1'}</span>
            Registro en <strong>5 minutos</strong>
          </p>
        </motion.div>
      </div>

      {/* ── RIGHT COLUMN (orange panel + rider + process steps) ── */}
      <motion.div
        className="wr__right"
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {/* Rider */}
        <div className="wr__rider-wrap">
          <WelcomeRider />
        </div>

        {/* Step card — cycles through process */}
        <div className="wr__step-card">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeProcess}
              className="wr__step-inner"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="wr__step-num">{currentStep.num}</div>
              <div className="wr__step-body">
                <span className="wr__step-title">{currentStep.title}</span>
                <span className="wr__step-desc">{currentStep.desc}</span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress dots */}
        <div className="wr__step-dots">
          {processSteps.map((s, i) => (
            <button
              key={i}
              className={`wr__step-dot ${i === activeProcess ? 'wr__step-dot--active' : ''}`}
              onClick={() => setActiveProcess(i)}
              aria-label={`Paso ${s.num}`}
            >
              <span className="wr__step-dot-icon">{s.icon}</span>
              {i === activeProcess && (
                <motion.span
                  className="wr__step-dot-bar"
                  layoutId="dotBar"
                  transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                />
              )}
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default StepWelcome;
